import { Skeleton } from "@nexus/ui";

/** The placeholder follows the content it stands for. */
export type WidgetSkeletonShape =
  | "kpi"
  | "kpis"
  | "chart"
  | "donut"
  | "table"
  | "list"
  | "cards"
  | "timeline"
  | "matrix"
  /** A proportion bar with its key: the summary form of a distribution. */
  | "share";

export interface WidgetSkeletonProps {
  shape: WidgetSkeletonShape;
  /** Accessible name of the busy region. */
  label?: string;
  className?: string;
}

const BAR_HEIGHTS = [58, 74, 46, 88, 66, 80, 52, 70];
const TIMELINE_STOPS = [12, 31, 52, 73, 91];
const SHARE_SEGMENTS = [52, 29, 17];

/**
 * Body of a widget that is waiting for its first data: simple shapes in the
 * place of the real content (values for KPIs, bars for a chart, rows for a
 * table…). Goes inside the widget frame, so the widget keeps its footprint.
 */
export function WidgetSkeleton({ shape, label = "Loading", className }: WidgetSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className={["flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden", className]
        .filter(Boolean)
        .join(" ")}
    >
      <SkeletonBody shape={shape} />
    </div>
  );
}

function SkeletonBody({ shape }: { shape: WidgetSkeletonShape }) {
  switch (shape) {
    case "kpi":
      return (
        <div className="flex flex-col gap-3 density-metric-padding">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-9 w-36 max-w-full" />
          <div className="flex items-center justify-between gap-3">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-5 w-12" />
          </div>
        </div>
      );
    case "kpis":
      return (
        <div className="@container">
          <div className="grid grid-cols-2 gap-px @4xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="flex min-w-0 flex-col gap-3 density-metric-padding">
                <Skeleton className="h-3 w-2/3 max-w-32" />
                <Skeleton className="h-7 w-1/3 min-w-14" />
              </div>
            ))}
          </div>
        </div>
      );
    case "chart":
      return (
        <div className="flex min-h-0 flex-1 items-end gap-2 density-widget-body">
          {BAR_HEIGHTS.map((height, index) => (
            <Skeleton
              key={index}
              className="flex-1 rounded-b-none"
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
      );
    case "donut":
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-5 density-widget-body">
          <div className="relative size-36">
            <Skeleton className="size-full rounded-full" />
            <span className="absolute inset-6 rounded-full bg-surface" aria-hidden="true" />
          </div>
          <div className="flex items-center gap-3">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-2.5 w-12" />
            ))}
          </div>
        </div>
      );
    case "table":
      return (
        <div className="flex flex-col density-widget-body">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <div
              key={index}
              className="flex items-center gap-4 border-b border-border py-2.5 last:border-b-0"
            >
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className={index % 2 ? "h-3.5 w-1/3" : "h-3.5 w-1/2"} />
              <Skeleton className="ml-auto h-3.5 w-12" />
            </div>
          ))}
        </div>
      );
    case "list":
      return (
        <div className="flex flex-col gap-3.5 density-widget-body">
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className="flex items-center gap-3">
              <Skeleton className="size-6 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <Skeleton className={index % 2 ? "h-3 w-1/2" : "h-3 w-2/3"} />
                <Skeleton className="h-2.5 w-1/3" />
              </div>
              <Skeleton className="h-3.5 w-10" />
            </div>
          ))}
        </div>
      );
    case "cards":
      return (
        <div className="grid grid-cols-1 gap-3 p-4 @xl/widget:grid-cols-2 @4xl/widget:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className={`flex flex-col gap-3 rounded-lg border border-border p-4 ${index > 0 ? "hidden @xl/widget:flex" : ""} ${index > 1 ? "@xl/widget:hidden @4xl/widget:flex" : ""}`}
            >
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-2.5 w-1/3" />
              <Skeleton className="mt-2 h-7 w-1/2" />
            </div>
          ))}
        </div>
      );
    case "timeline":
      return (
        <div className="relative flex min-h-32 flex-1 items-center px-6 density-widget-body">
          <Skeleton className="h-1 w-full" />
          {TIMELINE_STOPS.map((position, index) => (
            <div
              key={position}
              className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
              style={{ left: `${position}%` }}
            >
              <Skeleton className="size-3.5 rounded-full" />
              <Skeleton className={index % 2 ? "h-2.5 w-12" : "h-2.5 w-16"} />
            </div>
          ))}
        </div>
      );
    case "share":
      return (
        <div className="flex flex-col gap-2.5 density-widget-body">
          <div className="flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full">
            {SHARE_SEGMENTS.map((share) => (
              <Skeleton
                key={share}
                className="h-full rounded-none"
                style={{ flex: `${share} 1 0%` }}
              />
            ))}
          </div>
          <div className="flex items-center gap-5">
            {[0, 1, 2, 3].map((index) => (
              <div key={index} className="flex items-center gap-1.5">
                <Skeleton className="size-2 rounded-full" />
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-4 w-7" />
              </div>
            ))}
            <Skeleton className="ml-auto h-3 w-20" />
          </div>
        </div>
      );
    case "matrix":
      return (
        <div className="grid flex-1 grid-cols-[minmax(8rem,1.5fr)_repeat(4,minmax(4rem,1fr))] auto-rows-[minmax(2.5rem,1fr)] overflow-hidden density-widget-body">
          {Array.from({ length: 35 }, (_, index) => (
            <div key={index} className="flex items-center border-b border-border px-3">
              <Skeleton className={index % 5 === 0 ? "h-3 w-3/4" : "h-3 w-1/2"} />
            </div>
          ))}
        </div>
      );
  }
}
